import React, { useCallback, useEffect, useState, useRef, useContext } from 'react';
import { socket } from '../../context/socket';
import Message from '../message/Message';
import ChatBoxBottom from '../chatBoxBottom/ChatBoxBottom';
import { useConfettiText } from '../../hooks/useConfettiText';
import useMobileView from '../../hooks/useMobileView';
import useClickOutside from '../../clickOutside';
import { handleEmoji } from '../../utils/handleEmojis';
import { useIsTyping } from 'use-is-typing';
import { useReactMediaRecorder } from 'react-media-recorder';
import { AuthContext } from '../../context/AuthContext';
import axiosConn from '../../axiosConn';
import ChatBoxMiddle from '../../components/chatBoxMiddle/ChatBoxMiddle';
import { ViewPortContext } from '../../context/ViewPortContext';
import handleFileInput from '../../utils/handleFileInput';
import { debounce } from '../../context/ViewPortContext';
import WelcomeMessage from '../../components/welcomeMessage/WelcomeMessage';
import AdRotator from '../adRotator/AdRotator';
import { v4 as uuidv4 } from 'uuid';
import { MAX_VIDEO_SIZE, MAX_IMAGE_SIZE } from '../../config/constants';
import { INCOMING_MESSAGE_BL_URL } from '../../utils/constants';
import './galaxyChatBox.css';
import useGalaxyChatScroll, { galaxyScrollIdentity } from './useGalaxyChatScroll';

// NEW (Better-than-FAANG+ uploader)
import {
  createMediaUploader,
  DEFAULT_VIDEO_MIME_TYPES,
  MediaUploadError,
} from '../../utils/mediaUpload';

const MESSAGE_PARTICLE_COUNT = 100;
const maxVideoSize = MAX_VIDEO_SIZE;
const maxImageSize = MAX_IMAGE_SIZE;

const getStableId = (value) => {
  if (!value) return '';
  return String(value._id || value.id || value);
};

const listIncludesPlayer = (list = [], playerId) =>
  list.some((item) => getStableId(item) === String(playerId));

// Mirror Messenger's ID merge pattern, adding only pending-entry reconciliation.
export const reconcileGalaxyMessage = (messages, incoming) => {
  const list = Array.isArray(messages) ? messages : [];
  const matches = (message) =>
    (incoming._id && message._id === incoming._id) ||
    (message.isPending &&
      incoming.tempId &&
      message.tempId === incoming.tempId &&
      message.senderId === incoming.senderId);
  const index = list.findIndex(matches);
  if (index < 0) return [...list, incoming];
  return list.flatMap((message, i) =>
    i === index ? [{ ...message, ...incoming }] : matches(message) ? [] : [message],
  );
};

const GalaxyChatBox = ({
  galaxyId,
  senderId,
  senderProfilePhoto,
  senderPlayerId,
  galaxyMembers,
  subscriptionStatus = 'Subscribe',
  setProgress,
  galaxyChatOnlinePlayers,
  isPlayerInRole,
  setMessages,
  messages = [],
  isChatHistoryLoading = false,
  isChatHistoryLoaded = false,
  chatHistoryError = null,
  onRetryChatHistory,
  isGalaxyMember = false,
  onSubscribe,
  onCancelRequest,
  onUnsubscribe,
  membershipButtonLabel,
  className,
  galaxyName = '',
}) => {
  const { player } = useContext(AuthContext);
  const { width } = useContext(ViewPortContext);

  const [messageText, setMessageText] = useState('');
  const from = 'galaxyChat';
  const [voiceMemo, setVoiceMemo] = useState(false);
  const [picker, setPicker] = useState(false);
  const [isTyping, register] = useIsTyping({ timeout: 300 });
  const [isTypingDisplay, SetIsTypingDisplay] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendingFile, setSendingFile] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const { status, startRecording, stopRecording, mediaBlobUrl, clearBlobUrl } =
    useReactMediaRecorder({ audio: true });
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [error, setError] = useState('');
  const [offlineMembers, setOfflineMembers] = useState([]);

  const mobileView = useMobileView();
  const menu = useRef(null);
  const fileInput = useRef(null);
  const [cursorPosition, setCursorPosition] = useState();
  const [file, setFile] = useState(null);
  const textRef = useRef(null);
  const messageRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const chatContainerRef = useRef(null);
  const chatContentRef = useRef(null);
  const messageSoundRef = useRef();
  const sendingRef = useRef(false);
  const chatScopeRef = useRef('');
  const chatScope = `${galaxyId}:${senderId}:${player.token}:${subscriptionStatus}`;
  chatScopeRef.current = chatScope;
  useEffect(() => () => {
    chatScopeRef.current = '';
  }, []);

  const attachChatFile = useCallback(
    (incomingFile, { source = 'picker' } = {}) => {
      const result = handleFileInput(
        { target: { files: incomingFile ? [incomingFile] : [] } },
        setError,
        setFile,
        maxVideoSize,
        maxImageSize,
      );
      return {
        accepted: result?.accepted === true,
        errorCode: result?.errorCode || null,
        file: result?.file || null,
        source,
        message: result?.message,
      };
    },
    [],
  );

  const clearChatAttachment = useCallback(() => {
    setFile(null);
    if (fileInput.current) fileInput.current.value = '';
  }, []);

  // Uploader (singleton via ref)
  const uploaderRef = useRef(null);
  if (!uploaderRef.current) {
    // Extend video mime types to include audio for voice memos
    const customVideoTypes = new Set([
      ...DEFAULT_VIDEO_MIME_TYPES,
      'audio/mpeg',
      'audio/mpeg-3',
      'audio/mp3',
    ]);
    const customIsVideoName = (name = '') =>
      /\.(mp4|m4v|webm|ogg|ogv|mov|flv|3gp|3g2|avi|mkv|mp3)$/i.test(name);

    uploaderRef.current = createMediaUploader({
      maxImageBytes: MAX_IMAGE_SIZE,
      maxVideoBytes: MAX_VIDEO_SIZE,
      videoMimeTypes: customVideoTypes,
      isVideoName: customIsVideoName,
      forceAutoResourceType: true,
      enableAdaptiveLadder: false, // keep lean for chat
      enableGifOptimization: true,
      enableThumbnails: false, // optional for chat – disable to save time
      enableHashDedupe: true,
      debug: false,
      signed: false, // unsigned preset
    });
  }
  const uploader = uploaderRef.current;

  const chatMessages = Array.isArray(messages) ? messages : [];
  const canViewGalaxyChat = listIncludesPlayer(galaxyMembers, senderId);
  const showHistoryLoading =
    subscriptionStatus === 'Subscribed' && isChatHistoryLoading;
  const showHistoryError =
    subscriptionStatus === 'Subscribed' &&
    Boolean(chatHistoryError) &&
    chatMessages.length === 0;
  const showEmptyHistory =
    subscriptionStatus === 'Subscribed' &&
    isChatHistoryLoaded &&
    !showHistoryLoading &&
    !chatHistoryError &&
    chatMessages.length === 0;
  const { start: visibleStart, showMore, jumpToLatest, away } = useGalaxyChatScroll({
    messages: chatMessages,
    scope: `${chatScope}:${canViewGalaxyChat}`,
    active: canViewGalaxyChat,
    renderingMessages: !showHistoryLoading && !showHistoryError,
    viewportRef: chatContainerRef,
    contentRef: chatContentRef,
  });

  const handleClickOutside = () => {
    setPicker((prev) => !prev);
  };

  useClickOutside(menu, () => handleClickOutside());

  const [showConfettiText, ConfettiTextComponent] = useConfettiText(
    !mobileView ? (voiceMemo ? messageRef : textRef) : messageRef,
    mobileView,
    '+1 JGK So Fun!',
    MESSAGE_PARTICLE_COUNT,
    'messenger',
    voiceMemo,
  );

  useEffect(() => {
    if (cursorPosition && textRef !== null)
      textRef.current.selectionEnd = cursorPosition;
  }, [cursorPosition]);

  useEffect(() => {
    const newOfflineMembers = galaxyMembers.filter(
      (memberId) =>
        !galaxyChatOnlinePlayers.some(
          (chatPlayer) => chatPlayer.id === memberId,
        ),
    );
    setOfflineMembers(newOfflineMembers);
  }, [galaxyChatOnlinePlayers, galaxyMembers]);

  useEffect(() => {
    if (!messageSoundRef.current) {
      messageSoundRef.current = new Audio(INCOMING_MESSAGE_BL_URL);
    }

    if (!listIncludesPlayer(galaxyMembers, senderId)) {
      return;
    }

    const join = () => socket.emit('addPlayerToGalaxyChat', {
      galaxyId, playerId: senderId,
    });
    join();

    const handleNewMessage = (data) => {
      if (data?.galaxyId !== galaxyId || !data._id) {
        return;
      }
      setMessages((prevMessages) => reconcileGalaxyMessage(
        prevMessages, { ...data, isPending: false },
      ));

      if (data.senderId !== senderId) {
        const playPromise = messageSoundRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.log(error);
          });
        }
      }
    };

    socket.on('getGalaxyChatMessage', handleNewMessage);
    socket.on('connect', join);
    const handleBulkDelete = (data) => {
      if (data?.galaxyId === galaxyId) setMessages([]);
    };
    socket.on('getGalaxyMessagesDeleted', handleBulkDelete);

    return () => {
      socket.emit('removePlayerFromGalaxyChat', {
        galaxyId,
        playerId: senderId,
      });
      socket.off('getGalaxyChatMessage', handleNewMessage);
      socket.off('connect', join);
      socket.off('getGalaxyMessagesDeleted', handleBulkDelete);
    };
  }, [galaxyId, senderId, galaxyMembers]);

  const handleMicClick = () => {
    setVoiceMemo(true);
    if (!isRecording) {
      startRecording();
    } else {
      stopRecording();
    }
    setIsRecording((prev) => !prev);
  };

  const persistMessage = async (message, scope) => {
    if (chatScopeRef.current !== scope) return false;
    setMessages((previous) => reconcileGalaxyMessage(
      previous, { ...message, isPending: true },
    ));
    try {
      const response = await axiosConn.post(
        'galaxyMessages', { message, offlineMembers },
        { headers: { Authorization: `Bearer ${player.token}` } },
      );
      const saved = response?.data?.data?.data;
      if (!saved?._id || saved.galaxyId !== message.galaxyId) {
        throw new Error('Message status is uncertain. Refresh chat before sending again.');
      }
      if (chatScopeRef.current !== scope) return false;
      setMessages((previous) => reconcileGalaxyMessage(
        previous, { ...saved, isPending: false },
      ));
      return true;
    } catch (err) {
      if (chatScopeRef.current === scope) {
        setMessages((previous) => (Array.isArray(previous) ? previous : []).filter(
          (entry) => !(entry.isPending && entry.tempId === message.tempId),
        ));
      }
      throw err;
    }
  };

  const sendError = (err) => err.response?.status === 403
    ? 'You no longer have access to this Galaxy chat. Refresh to check your membership.'
    : err.response?.status === 401
      ? 'Your session has expired. Sign in again to send this message.'
      : err.response?.data?.message || err.message || 'Could not send message.';

  // ============ NEW: Use mediaUpload utility for audio (voice memo) ============
  const handleSendAudioMemo = async () => {
    if (chatScopeRef.current !== chatScope) return;
    if (!listIncludesPlayer(galaxyMembers, senderId) || sendingRef.current) return;

    if (!mediaBlobUrl) {
      setVoiceMemo(false);
      return;
    }

    jumpToLatest();
    let transformedUrl = '';
    const scope = chatScopeRef.current;
    sendingRef.current = true;
    setError('');
    try {
      setLoading(true);
      setProgress(0);
      setUploadPercentage(0);

      const audioBlob = await fetch(mediaBlobUrl).then((r) => r.blob());
      const fileName = Date.now() + '.mp3';
      const audioFile = new File([audioBlob], fileName, { type: 'audio/mpeg' });

      // Validate (will throw if unsupported)
      uploader.validate(audioFile);

      const result = await uploader.upload(audioFile, {
        publicIdPrefix: 'GalaxyChat/Audio',
        onProgress: (pct) => {
          setProgress(pct);
          setUploadPercentage(pct);
        },
        transform: true,
      });

      transformedUrl = result.transformedUrl;
      setProgress(100);
      setUploadPercentage(100);
    } catch (e) {
      setLoading(false);
      setError(e.message || 'Audio upload failed.');
      sendingRef.current = false;
      return;
    }

    const message = {
      galaxyId,
      senderId: player.data.player._id,
      text: messageText,
      img: transformedUrl,
      senderProfilePhoto: player.data.player.profilePhoto,
      senderPlayerId: player.data.player.playerId,
      createdAt: new Date(),
      tempId: uuidv4(),
    };

    try {
      if (!(await persistMessage(message, scope))) return;
      setMessageText('');
      setFile(null);
      if (fileInput.current) fileInput.current.value = null;
      showConfettiText();
      setVoiceMemo(false);
      clearBlobUrl();
    } catch (err) {
      setError(sendError(err));
    } finally {
      setLoading(false);
      sendingRef.current = false;
    }
  };

  // ============ NEW: Use mediaUpload utility for image/video attachments ============
  const handleSubmit = async (e) => {
    if (sendingRef.current || chatScopeRef.current !== chatScope) return;
    if (
      !listIncludesPlayer(galaxyMembers, senderId) &&
      subscriptionStatus !== 'Subscribed'
    ) {
      return;
    }

    if (
      (e.key === 'Enter' || e.type === 'click') &&
      (messageText.trim() !== '' || file) // CHANGED: allow send when file state present
    ) {
      sendingRef.current = true;
      setLoading(true);
      setError('');
      let transformedUrl = '';
      const scope = chatScopeRef.current;

      if (file) {
        try {
          setLoading(true);
          setSendingFile(true);
          setProgress(0);
          setUploadPercentage(0);

          // Validate via uploader (size/type gates)
          uploader.validate(file);

          const { transformedUrl: tUrl } = await uploader.upload(file, {
            publicIdPrefix: 'GalaxyChat/Media',
            onProgress: (pct) => {
              setProgress(pct);
              setUploadPercentage(pct);
            },
            transform: true,
          });

          transformedUrl = tUrl;
        } catch (e) {
          sendingRef.current = false;
          setLoading(false);
          setSendingFile(false);
          if (e instanceof MediaUploadError) {
            setError(e.message);
          } else {
            setError(e.message || 'Upload failed.');
          }
          return;
        }
      }

      const message = {
        galaxyId,
        senderId: player.data.player._id,
        text: messageText.trim(),
        img: transformedUrl,
        senderProfilePhoto: player.data.player.profilePhoto,
        senderPlayerId: player.data.player.playerId,
        createdAt: new Date(),
        tempId: uuidv4(),
      };

      try {
        if (!(await persistMessage(message, scope))) return;

        setLoading(false);
        setMessageText('');
        setFile(null);
        if (fileInput.current) fileInput.current.value = '';
        setSendingFile(false);
        setProgress(100);
        setUploadPercentage(100);
        showConfettiText();
      } catch (err) {
        setError(sendError(err));
      } finally {
        setLoading(false);
        setSendingFile(false);
        sendingRef.current = false;
      }
    }
  };

  const debouncedHandleSubmit = debounce(handleSubmit, 700);
  // Capture deliberate Send intent now, before debounce/upload. Later pending,
  // HTTP and socket updates follow the reader's most recent scroll intent.
  const submitFromComposer = (event, deferred = false) => {
    if (!sendingRef.current && chatScopeRef.current === chatScope &&
      canViewGalaxyChat && (event.key === 'Enter' || event.type === 'click') &&
      (messageText.trim() || file)) jumpToLatest();
    return deferred ? debouncedHandleSubmit(event) : handleSubmit(event);
  };

  const handleDeleteMessage = (messageId) => {
    setMessages((previous) =>
      (Array.isArray(previous) ? previous : []).filter(
        (m) => m._id !== messageId && m.tempId !== messageId,
      ),
    );
  };

  return (
    <>
      {(subscriptionStatus === 'Subscribe' ||
        subscriptionStatus === 'Requested') && (
        <>
          <div className="centered-column-galaxyChatBox">
            <WelcomeMessage
              subscriptionStatus={subscriptionStatus}
              playerId={player.data.player.playerId}
              isPlayerInRole={isPlayerInRole}
              onSubscribe={onSubscribe}
              onCancelRequest={onCancelRequest}
              onUnsubscribe={onUnsubscribe}
              membershipButtonLabel={membershipButtonLabel}
              className={className}
              galaxyName={galaxyName}
            />
            {subscriptionStatus === 'Subscribed' && (
              <AdRotator surfaceKey="chat" />
            )}
          </div>
        </>
      )}
      <div className="galaxy-chat-viewport-shell">
      <div className="chat-container" ref={chatContainerRef}>
      <div className="galaxy-chat-content" ref={chatContentRef}>
        {subscriptionStatus === 'Subscribed' &&
          visibleStart > 0 && (
            <div
              className={
                !mobileView
                  ? 'viewMessages load-more-messages'
                  : 'viewMessagesMobile load-more-messages'
              }
              onClick={() => showMore()}
            >
              View {visibleStart} more messages
            </div>
          )}
        {showHistoryLoading && (
          <div className="galaxy-chat-state" role="status">
            <p className="galaxy-chat-state__title">Loading chat history...</p>
          </div>
        )}
        {showHistoryError && (
          <div className="galaxy-chat-state galaxy-chat-state--error">
            <p className="galaxy-chat-state__title">
              Could not load chat history.
            </p>
            <p className="galaxy-chat-state__body">
              Your composer still works, but the previous messages need a
              refresh.
            </p>
            {typeof onRetryChatHistory === 'function' && (
              <button
                type="button"
                className="galaxy-chat-state__retry"
                onClick={onRetryChatHistory}
              >
                Retry
              </button>
            )}
          </div>
        )}
        {showEmptyHistory && (
          <div className="galaxy-chat-state">
            <p className="galaxy-chat-state__title">
              No Galaxy messages yet.
            </p>
            <p className="galaxy-chat-state__body">
              Start the first conversation for this Galaxy.
            </p>
          </div>
        )}
        {!showHistoryLoading &&
          !showHistoryError &&
          chatMessages
            .slice(visibleStart)
            .map((message) => {
              const isOwnMessage = message.senderId === senderId;
              return (
                canViewGalaxyChat && (
                  <div
                    key={message._id || message.tempId}
                    data-galaxy-scroll-id={galaxyScrollIdentity(message)}
                    className={isOwnMessage ? 'message-own' : 'message-other'}
                  >
                    <Message
                      message={message}
                      own={isOwnMessage}
                      senderProfilePhoto={message.senderProfilePhoto}
                      from="galaxyChat"
                      onDeleteMessage={handleDeleteMessage}
                      galaxyId={galaxyId}
                      isPlayerInRole={isPlayerInRole}
                    />
                    {message.isPending && <span role="status">Sending...</span>}
                  </div>
                )
              );
            })}
        <div className="bottomMessages" />
        <div className="chat-input">
          <ConfettiTextComponent />
        </div>
        <ChatBoxMiddle
          isTypingDisplay={isTypingDisplay}
          senderPlayerId={senderPlayerId}
          status={status}
          mediaBlobUrl={mediaBlobUrl}
          loading={loading}
          handleMicClick={handleMicClick}
          isRecording={isRecording}
          mobileView={mobileView}
          width={width}
          setVoiceMemo={setVoiceMemo}
          clearBlobUrl={clearBlobUrl}
          handleSendAudioMemo={handleSendAudioMemo}
          fileInput={fileInput}
          attachChatFile={attachChatFile}
          clearChatAttachment={clearChatAttachment}
          handleFileInput={(e) =>
            handleFileInput(e, setError, setFile, maxVideoSize, maxImageSize)
          }
          error={error}
          setError={setError}
          setFile={setFile}
          setText={setMessageText}
          file={file}
          setLoading={setLoading}
          setIsLoading={setIsLoading}
          setSendingFile={setSendingFile}
          messageRef={messageRef}
          chatContainerRef={chatContainerRef}
          handleSubmit={submitFromComposer}
        />
      </div>
      </div>
      {away && (
        <button type="button" className="galaxy-chat-jump" onClick={jumpToLatest}>
          Jump to latest
        </button>
      )}
      </div>

      <div className="chatBoxBottomContainer">
        {!voiceMemo && isGalaxyMember && (
          <ChatBoxBottom
            voiceMemo={voiceMemo}
            picker={picker}
            menu={menu}
            handleEmoji={(e, { emoji }) =>
              handleEmoji(
                e,
                { emoji },
                messageText,
                setMessageText,
                textRef,
                setCursorPosition,
              )
            }
            textRef={textRef}
            register={register}
            text={messageText}
            setText={setMessageText}
            handleSubmit={(event) => submitFromComposer(event, true)}
            loading={loading}
            sendingFile={sendingFile}
            handleMicClick={handleMicClick}
            isRecording={isRecording}
            setPicker={setPicker}
            fileInput={fileInput}
            attachChatFile={attachChatFile}
            setSendingFile={setSendingFile}
            handleSendAudioMemo={handleSendAudioMemo}
            parent="GalaxyChatBox"
          />
        )}
      </div>
    </>
  );
};

export default GalaxyChatBox;
